import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  AppBar,
  Toolbar,
  TextField,
  Chip,
  CircularProgress,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  Business,
  Star,
  Security,
  Speed,
  TrendingUp,
  ContactMail,
  CalendarToday,
  LocationOn,
  ArrowForward,
  Email,
  Menu as MenuIcon,
  Close,
} from '@mui/icons-material';
import api from '../../services/api';
import { useResponsive } from '../../hooks/useResponsive';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.paceestate.com';

const PublicHome = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [upcomingSales, setUpcomingSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  const features = [
    {
      icon: <Star sx={{ fontSize: 48 }} />,
      title: 'Professional Excellence',
      description: 'Over 20 years of experience in estate sales and liquidation services across Florida.',
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: 'Trust & Integrity',
      description: 'Fully licensed, bonded, and insured for your peace of mind.',
    },
    {
      icon: <Speed sx={{ fontSize: 48 }} />,
      title: 'Fast Turnaround',
      description: 'Quick and efficient sales process from start to finish.',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 48 }} />,
      title: 'Maximum Value',
      description: 'Expert appraisal and pricing to maximize your returns.',
    },
  ];

  useEffect(() => {
    fetchUpcomingSales();
  }, []);

  const fetchUpcomingSales = async () => {
    try {
      const response = await api.get('/public/sales/upcoming', { timeout: 10000 });
      setUpcomingSales(response.data.sales || []);
    } catch (error) {
      console.error('Error fetching upcoming sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = useCallback(async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactError('');

    try {
      let recaptchaToken = null;

      // Try to get reCAPTCHA token with timeout, but don't fail if unavailable
      if (executeRecaptcha) {
        try {
          const recaptchaPromise = executeRecaptcha('contact_form');
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('reCAPTCHA timeout')), 5000)
          );
          recaptchaToken = await Promise.race([recaptchaPromise, timeoutPromise]);
        } catch (recaptchaError) {
          console.warn('reCAPTCHA skipped:', recaptchaError.message);
        }
      }

      // Use api service with explicit timeout
      const response = await api.post('/public/contact', {
        ...contactForm,
        recaptcha_token: recaptchaToken,
      }, {
        timeout: 15000,
      });

      if (response.data) {
        setContactSuccess(true);
        setContactForm({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setContactSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      let errorMsg = 'Failed to submit. Please try again.';

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = 'Request timed out. Please try again.';
      } else if (error.isOffline) {
        errorMsg = 'You appear to be offline. Please check your connection.';
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }

      setContactError(errorMsg);
    } finally {
      setContactSubmitting(false);
    }
  }, [executeRecaptcha, contactForm]);

  const formatSaleDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Box>
      {/* Header */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Business sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            PaceEstate
          </Typography>
          {isMobile ? (
            <>
              <IconButton color="inherit" onClick={() => setMobileMenuOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="right"
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
              >
                <Box sx={{ width: 250, pt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 1 }}>
                    <IconButton onClick={() => setMobileMenuOpen(false)}>
                      <Close />
                    </IconButton>
                  </Box>
                  <List>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => { navigate('/public/sales'); setMobileMenuOpen(false); }}>
                        <ListItemText primary="Upcoming Sales" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                        <ListItemText primary="Staff Login" />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Box>
              </Drawer>
            </>
          ) : (
            <>
              <Button color="inherit" onClick={() => navigate('/public/sales')}>
                Upcoming Sales
              </Button>
              <Button color="inherit" onClick={() => navigate('/login')} sx={{ ml: 2 }}>
                Staff Login
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #003366 0%, #1a4d7a 100%)',
          color: 'white',
          py: { xs: 6, sm: 8, md: 12 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            fontWeight="bold"
            sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3.75rem' } }}
          >
            Professional Estate Sales
          </Typography>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mb: { xs: 3, sm: 4 },
              opacity: 0.9,
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' }
            }}
          >
            Serving Florida with Excellence Since 2003
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: { xs: 3, sm: 4 },
              opacity: 0.8,
              display: { xs: 'none', sm: 'block' },
              fontSize: { sm: '1rem', md: '1.25rem' }
            }}
          >
            Full-service estate liquidation, downsizing, and property management solutions
          </Typography>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Button
              variant="contained"
              size={isMobile ? "medium" : "large"}
              color="secondary"
              href="#contact"
              sx={{ px: { xs: 3, sm: 4 }, py: { xs: 1, sm: 1.5 }, width: { xs: '100%', sm: 'auto' } }}
              startIcon={!isMobile && <ContactMail />}
              fullWidth={isMobile}
            >
              {isMobile ? "Free Consultation" : "Get Free Consultation"}
            </Button>
            <Button
              variant="outlined"
              size={isMobile ? "medium" : "large"}
              onClick={() => navigate('/public/sales')}
              sx={{
                color: 'white',
                borderColor: 'white',
                px: { xs: 3, sm: 4 },
                py: { xs: 1, sm: 1.5 },
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
              fullWidth={isMobile}
            >
              View Upcoming Sales
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Upcoming Sales Section */}
      {upcomingSales.length > 0 && (
        <Container sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={{ xs: 3, sm: 4 }} gap={2}>
            <div>
              <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
                Upcoming Estate Sales
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Browse our featured upcoming sales
              </Typography>
            </div>
            <Button
              variant="outlined"
              endIcon={!isMobile && <ArrowForward />}
              onClick={() => navigate('/public/sales')}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              View All Sales
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {upcomingSales.map((sale) => (
                <Grid item xs={12} sm={6} md={4} key={sale.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                        transition: 'all 0.3s ease-in-out',
                      },
                    }}
                    onClick={() => navigate(`/public/sales/${sale.id}`)}
                  >
                    {sale.primary_photo && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={`${API_URL}${sale.primary_photo.file_path}`}
                        alt={sale.name}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {sale.name}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formatSaleDate(sale.start_date)}
                          {sale.end_date && ` - ${formatSaleDate(sale.end_date)}`}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {sale.city}, {sale.state}
                        </Typography>
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Button size="small" color="primary" endIcon={<ArrowForward />}>
                        View Details
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      )}

      {/* Features Section */}
      <Container sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
          Why Choose PaceEstate?
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: { xs: 4, sm: 6 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Professional estate sales management you can trust
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Box sx={{ color: 'secondary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 8 }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
                Our Services
              </Typography>
              <Typography variant="h6" color="text.secondary" paragraph>
                Comprehensive Estate Solutions
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                From full estate sales to downsizing assistance, we provide complete support
                throughout the entire process. Our experienced team handles everything from
                appraisal and pricing to marketing, sale execution, and cleanup.
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                We specialize in estate liquidation, downsizing services, property cleanouts,
                antique appraisals, and consignment sales. Our goal is to maximize value while
                minimizing stress during difficult transitions.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, backgroundColor: 'primary.main', color: 'white' }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Areas We Serve
                </Typography>
                <Typography variant="body1" paragraph>
                  Proudly serving communities throughout all of Florida:
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ paddingLeft: 20, columns: 2 }}>
                    <li>Tallahassee & Leon County</li>
                    <li>Jacksonville & Duval County</li>
                    <li>Pensacola & Escambia County</li>
                    <li>Orlando & Orange County</li>
                    <li>Panama City & Bay County</li>
                    <li>Tampa & Hillsborough County</li>
                    <li>Gainesville & Alachua County</li>
                    <li>Miami & Miami-Dade County</li>
                    <li>Fort Lauderdale & Broward County</li>
                    <li>West Palm Beach & Palm Beach County</li>
                    <li>Sarasota & Sarasota County</li>
                    <li>Naples & Collier County</li>
                  </ul>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                  And all surrounding areas statewide
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Container sx={{ py: 8 }} id="contact">
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
              Get in Touch
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Ready to get started? Contact us for a free consultation
            </Typography>

            <Box sx={{ mt: 4 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Email color="primary" />
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="h6">
                    info@paceestate.com
                  </Typography>
                </div>
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <LocationOn color="primary" />
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Serving
                  </Typography>
                  <Typography variant="h6">
                    All of Florida - Statewide
                  </Typography>
                </div>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Request Free Consultation
              </Typography>

              {contactSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Thank you! We'll get back to you soon.
                </Alert>
              )}

              {contactError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {contactError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleContactSubmit}>
                <TextField
                  fullWidth
                  label="Your Name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={contactSubmitting}
                >
                  {contactSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: 'primary.main', color: 'white' }}>
        <Container>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                PaceEstate
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Professional estate sales and liquidation services serving Florida since 2003.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Quick Links
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Button color="inherit" sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/public/sales')}>
                  Upcoming Sales
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start' }} href="#contact">
                  Contact Us
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/login')}>
                  Staff Login
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Contact Info
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Email: info@paceestate.com
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
            <Typography variant="body2" align="center" sx={{ opacity: 0.8 }}>
              © {new Date().getFullYear()} PaceEstate. All rights reserved. Licensed & Insured.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicHome;
